import { __decorate } from "tslib";
// NPM
import { Component, Mixins } from "vue-property-decorator";
import Switches from "vue-switches";
import { interpret } from "xstate";
// S1
import BaseMixer from "@/router/views/BaseMixer";
import { strictDateFormatter } from "@/utilities/strictDateFormatter";
import { toaster } from "@/utilities/toaster";
import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header.vue";
import SectionLoader from "@/components/widgets/SectionLoader.vue";
import { clientRiskColorIcon, clientRiskColorName, clientRiskLevelName } from "@/store/models/client/clientRisk";
import ClientModule from "@/store/modules/client.module";
import ClientUpdatesModule from "@/store/modules/clientUpdates.module";
import weeklyStandupMachine, { DEFAULT_LAST_COUNT, LAST_COUNT_OPTIONS } from "./machines/WeeklyStandupMachine";
import { formatDate, getInitials, isAfterToday, parseDate, updateText } from "./updatesHelpers";
let WeeklyStandup = /** @class */ (() => {
    let WeeklyStandup = class WeeklyStandup extends Mixins(BaseMixer) {
        constructor() {
            super(...arguments);
            this.machine = weeklyStandupMachine;
            this.machineService = interpret(this.machine, { devTools: true });
            this.state = this.machine.initialState;
            this.strictDateFormatter = strictDateFormatter;
            this.formatDate = formatDate;
            this.getInitials = getInitials;
            // Plain text of the update's HTML, one line per paragraph
            this.updateText = updateText;
            this.title = "Weekly Standup";
            this.items = [
                {
                    text: "Home",
                    to: "/dashBoard",
                },
                {
                    text: "Customer Service",
                },
                {
                    text: "Weekly Standup",
                    active: true,
                },
            ];
            this.columns = [
                { key: "csm", label: "CSM — Updates" },
                { key: "tsm", label: "TSM — Updates" },
            ];
            // Ribbon: Recent [count], or a date range. The inactive switch only changes which clients are listed.
            this.countOptions = LAST_COUNT_OPTIONS;
            this.period = "lastCount";
            this.count = DEFAULT_LAST_COUNT;
            this.dateRange = [];
            // What the report on screen was actually generated for (the picker can move ahead of it)
            this.generatedPeriod = "lastCount";
            this.generatedCount = DEFAULT_LAST_COUNT;
            this.generatedFrom = null;
            this.generatedTo = null;
            // Same as the Client listing: off lists the active clients only, on the inactive clients only.
            // A new period or range keeps the toggle as is.
            this.showInactive = false;
            // Update texts taller than the clamp (they get Show more), and the ones the user expanded
            this.overflowingIds = [];
            this.expandedIds = [];
            // Manager photos that failed to load - they fall back to initials
            this.brokenImageURLs = [];
            // First load: the page loader stays up until the first report has arrived
            this.initialLoadDone = false;
        }
        send(event) {
            this.machineService.send(event);
        }
        // Store
        get rows() {
            return ClientUpdatesModule.weeklyStandup;
        }
        // The toggle swaps the list: active clients, or (toggle on) the inactive ones - never both
        get sections() {
            return this.showInactive
                ? [{ key: "inactive", rows: this.rows.filter((row) => !row.isActive), emptyText: "No inactive clients found." }]
                : [{ key: "active", rows: this.rows.filter((row) => row.isActive), emptyText: "No active clients found." }];
        }
        // View state
        get isLoading() {
            return this.state.matches("reportInfo.gettingReport");
        }
        // What the report on screen was generated for. The pickers can move ahead of it.
        get generatedPeriodLabel() {
            if (this.generatedPeriod === "custom" && this.generatedFrom && this.generatedTo) {
                return `${this.formatDate(this.generatedFrom)} – ${this.formatDate(this.generatedTo)}`;
            }
            return this.generatedCount === 1 ? "last 1 update" : `last ${this.generatedCount} updates`;
        }
        // Ribbon
        // Each option resets the other's value: Last Count clears the date range, Custom puts the count back to its default
        selectPeriod(key) {
            this.period = key;
            if (key === "lastCount") {
                this.dateRange = [];
                this.generateCountReport();
            }
            else {
                this.count = DEFAULT_LAST_COUNT;
            }
        }
        // A new count loads the report immediately
        onCountChange() {
            this.generateCountReport();
        }
        generateCountReport() {
            this.generatedPeriod = "lastCount";
            this.generatedCount = this.count;
            this.send({ type: "GENERATE_REPORT", count: this.count });
        }
        // No future days, and the end can't fall before the start
        disableRangeDate(date, currentValue) {
            const start = parseDate((currentValue || [])[0]);
            const pickingEnd = !!start && !parseDate((currentValue || [])[1]);
            return isAfterToday(date) || (pickingEnd && date < start);
        }
        // A completed custom range loads the report immediately
        onDateRangeChange() {
            this.generateReport();
        }
        // A typed range the strict parser rejected (impossible date or outside the allowed days) - the picker keeps its old range
        onDateRangeInputError(text) {
            toaster.ShowError(`“${text}” isn't a valid date range. Use ${this.dateFormat} for both dates.`);
        }
        generateReport() {
            const from = parseDate((this.dateRange || [])[0]);
            const to = parseDate((this.dateRange || [])[1]);
            if (from && to && from <= to) {
                this.generatedPeriod = this.period;
                this.generatedFrom = new Date(from.getFullYear(), from.getMonth(), from.getDate());
                this.generatedTo = new Date(to.getFullYear(), to.getMonth(), to.getDate());
                this.send({ type: "GENERATE_REPORT", dateFrom: from, dateTo: to });
            }
        }
        // The inactive list's clamped texts render now, so they need measuring for Show more
        onShowInactiveChange() {
            this.measureOverflow();
        }
        // Client cell
        // ws-risk-red / -orange / -green; a level without a colour of its own (ws-risk-other) keeps the plain grey chip
        riskClass(color) {
            return color ? `ws-risk-${clientRiskColorName(color)}` : "ws-risk-none";
        }
        riskTitle(color) {
            const label = color ? clientRiskLevelName(ClientModule.riskLevels, color) : "";
            return label ? `Risk: ${label}` : "No risk recorded";
        }
        riskIcon(color) {
            return color ? clientRiskColorIcon(color) : "";
        }
        // The Updates page with this client selected
        updatesLink(row) {
            return { name: "Updates", query: { clientId: row.clientId.toString() } };
        }
        managersOf(row) {
            return [
                { role: "CSM", name: row.csmName || null, imageURL: row.csmImageURL || null },
                { role: "TSM", name: row.tsmName || null, imageURL: row.tsmImageURL || null },
            ];
        }
        showManagerImage(manager) {
            return !!manager.imageURL && this.brokenImageURLs.indexOf(manager.imageURL) === -1;
        }
        onManagerImageError(manager) {
            this.brokenImageURLs = [...this.brokenImageURLs, manager.imageURL];
        }
        // Name stays on the photo tooltip; the row only shows the role
        managerTitle(manager) {
            return manager.name ? manager.name : "Unassigned";
        }
        // Updates
        updatesOf(row, column) {
            return (column === "csm" ? row.csmUpdates : row.tsmUpdates) || [];
        }
        updateKey(update) {
            return update.id.toString();
        }
        isOverflowing(update) {
            return this.overflowingIds.indexOf(this.updateKey(update)) > -1;
        }
        isExpanded(update) {
            return this.expandedIds.indexOf(this.updateKey(update)) > -1;
        }
        toggleExpanded(update) {
            const key = this.updateKey(update);
            this.expandedIds = this.isExpanded(update) ? this.expandedIds.filter((id) => id !== key) : [...this.expandedIds, key];
        }
        // Finds the clamped texts that are cut off, once the report has rendered (and again on resize)
        measureOverflow() {
            this.$nextTick(() => {
                if (!this.$el || !this.$el.querySelectorAll) {
                    return;
                }
                const ids = [];
                this.$el.querySelectorAll(".ws-update-text.is-clamped").forEach((element) => {
                    if (element.scrollHeight > element.clientHeight + 1) {
                        ids.push(element.getAttribute("data-update-id"));
                    }
                });
                // Expanded texts aren't clamped, so they can't be measured - they keep their Show less
                this.overflowingIds = [...ids, ...this.expandedIds.filter((id) => ids.indexOf(id) === -1 && this.overflowingIds.indexOf(id) > -1)];
            });
        }
        onWindowResize() {
            this.measureOverflow();
        }
        created() {
            // Start clean - a late response from a previous visit may have written to the store after it closed
            ClientUpdatesModule.ResetState();
            // Risk level names for the client-name tooltip
            ClientModule.GetRiskLevels();
            this.machineService.onTransition((state) => {
                const finishedLoading = this.state && this.state.matches("reportInfo.gettingReport") && !state.matches("reportInfo.gettingReport");
                this.state = state;
                if (!this.initialLoadDone && !state.matches("reportInfo.gettingReport")) {
                    this.initialLoadDone = true;
                }
                if (finishedLoading) {
                    this.expandedIds = [];
                    this.overflowingIds = [];
                    this.measureOverflow();
                }
            });
            this.machineService.start();
        }
        mounted() {
            window.addEventListener("resize", this.onWindowResize);
        }
        beforeDestroy() {
            window.removeEventListener("resize", this.onWindowResize);
            this.machineService.stop();
            ClientUpdatesModule.ResetState();
        }
    };
    WeeklyStandup = __decorate([
        Component({
            name: "WeeklyStandup",
            components: {
                Layout,
                PageHeader,
                SectionLoader,
                Switches,
            },
        })
    ], WeeklyStandup);
    return WeeklyStandup;
})();
export default WeeklyStandup;
